import { useState, type CSSProperties, type PointerEvent } from 'react'
import type { RatioCard } from '../../../../data/ServerApiClient'
import { formatAsOf, formatRatioValue, isRatioValueOutOfRange, normalizeCurrencyTextCompact, ratioToneForAssessment, type RatioTone } from '../cfoFormat'
import { ratioBenchmarkAnchor, ratioBenchmarkSource, type RatioBenchmarkSource } from '../cfoRatioBenchmarking'
import { buildRatioComparisonRows } from '../cfoRatioComparisonRows'
import { pinnedRatioCardSnapshot } from '../presentation/ratioCardBlock'
import { PinToReportButton } from '../presentation/useAddToReport'
import { DualAxisTrendLine } from '../charts/DualAxisTrendLine'
import { RatioAiChangeBadge } from './RatioAiChangeBadge'

// The full ratio "detail card" — health pill, formula, latest/avg/benchmark
// stats, a 12-month trend vs IRS benchmark, a comparison table, and component
// breakdown. Extracted from CfoRatiosPage so it can be embedded both on the
// Ratios page and inside the guided-analysis modal (the "drill down further"
// surface). The pure ratio helpers travel with it so both sites stay in sync.

export type RatioWindow = 'latest' | 'trailing_3' | 'trailing_6' | 'trailing_12'

export const RATIO_WINDOW_OPTIONS: Array<{ id: RatioWindow; label: string; periods: number | null }> = [
  { id: 'trailing_12', label: 'Trailing 12 mo', periods: 12 },
  { id: 'trailing_6', label: 'Trailing 6 mo', periods: 6 },
  { id: 'trailing_3', label: 'Trailing 3 mo', periods: 3 },
  { id: 'latest', label: 'Latest month', periods: null },
]

// The anchor implementation lives in cfoRatioBenchmarking.ts (non-React module, shared with the
// presentation export bundle) so it cannot drift from ratioBenchmarkSource; re-exported here so
// existing imports keep working.
export { ratioBenchmarkAnchor }

export const ratioWindowOption = (window: RatioWindow) => (
  RATIO_WINDOW_OPTIONS.find((option) => option.id === window) || RATIO_WINDOW_OPTIONS[0]
)

/** Human label for the benchmark stat/guide, matched to where the anchor comes from. */
export const ratioBenchmarkSourceLabel = (source: RatioBenchmarkSource | null): string => {
  if (source?.kind === 'history') return `Own ${source.tag}`
  if (source?.kind === 'band') return 'Healthy benchmark'
  return 'IRS average'
}

const averageFinite = (values: Array<number | null | undefined>): number | null => {
  const finite = values.filter((value): value is number => value != null && Number.isFinite(value))
  if (!finite.length) return null
  return finite.reduce((sum, value) => sum + value, 0) / finite.length
}

export const ratioCompanyValueForWindow = (ratio: RatioCard, window: RatioWindow): number | null => {
  const option = ratioWindowOption(window)
  if (option.periods == null) return ratio.latest
  const values = (ratio.trend || []).map((point) => point.value).slice(-option.periods)
  return averageFinite(values)
}

// Longest-to-shortest trailing windows used for the "Latest month" secondary stat.
const RATIO_TRAILING_CONTEXT_WINDOWS: RatioWindow[] = ['trailing_12', 'trailing_6', 'trailing_3']

// When the view is "Latest month", the primary "Latest" stat already shows ratio.latest, so a second
// "Latest month" stat would just duplicate it. Surface a trailing average for context instead: prefer
// trailing-12, but if the trend history is too short to actually cover 12 months, step down to the
// longest window that IS fully covered (6 mo, then 3 mo) so the label never overstates the window.
// Returns null when fewer than 3 finite months of history exist (secondary stat then renders "—").
export const ratioTrailingContext = (ratio: RatioCard): { window: RatioWindow; value: number } | null => {
  const values = (ratio.trend || []).map((point) => point.value)
  for (const window of RATIO_TRAILING_CONTEXT_WINDOWS) {
    const periods = ratioWindowOption(window).periods
    if (periods == null) continue
    const finite = values.slice(-periods).filter((value): value is number => value != null && Number.isFinite(value))
    if (finite.length >= periods) {
      return { window, value: finite.reduce((sum, value) => sum + value, 0) / finite.length }
    }
  }
  return null
}

const ratioTrendTail = (ratio: Pick<RatioCard, 'trend'>, count = 12) => (ratio.trend || []).slice(-count)

export const ratioTone = (ratio: RatioCard): RatioTone => ratioToneForAssessment(ratio.assessment)

export const ratioDirectionGlyph = (ratio: RatioCard): { glyph: string; color: string } => {
  switch (ratio.trend_direction) {
    case 'improving':
      return { glyph: '▲', color: 'var(--good-acc)' }
    case 'declining':
      return { glyph: '▼', color: 'var(--neg)' }
    case 'stable':
      return { glyph: '●', color: 'var(--muted)' }
    default:
      return { glyph: '', color: 'var(--muted)' }
  }
}

export const ratioDirectionLabel = (ratio: RatioCard): string => {
  switch (ratio.trend_direction) {
    case 'improving':
      return '12-mo improving'
    case 'declining':
      return '12-mo declining'
    case 'stable':
      return '12-mo stable'
    default:
      return ''
  }
}

export const formatShortMonth = (period: string | null | undefined): string => {
  if (!period) return ''
  const parsed = new Date(`${String(period).slice(0, 10)}T00:00:00Z`)
  if (Number.isNaN(parsed.getTime())) return String(period).slice(0, 7)
  return parsed.toLocaleDateString(undefined, { month: 'short', timeZone: 'UTC' })
}

export function RatioStat({
  label,
  value,
  accent = 'var(--ink)',
  large = false,
  selected = false,
  onClick,
}: {
  label: string
  value: string
  accent?: string
  large?: boolean
  selected?: boolean
  onClick?: () => void
}) {
  const style: CSSProperties = {
    border: selected ? '1px solid var(--brand)' : '1px solid var(--hair)',
    borderRadius: 'var(--r1)',
    padding: large ? '14px 16px' : '10px 12px',
    background: selected ? 'var(--wash)' : 'var(--paper)',
    boxShadow: selected ? '0 0 0 2px rgba(54, 94, 125, 0.12)' : undefined,
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    maxWidth: '100%',
    minWidth: 0,
    overflow: 'hidden',
    textAlign: 'left' as const,
    width: '100%',
  }
  const content = (
    <>
      <div className={`fg-ratio-stat-label${large ? ' is-large' : ''}`}>{label}</div>
      <div className={`fg-ratio-stat-value${large ? ' is-large' : ''}`} style={{ color: accent }}>{value}</div>
    </>
  )
  if (!onClick) return <div className={`fg-ratio-stat${large ? ' is-large' : ''}`} style={style}>{content}</div>
  return (
    <button
      type="button"
      className={`fg-ratio-stat${large ? ' is-large' : ''}`}
      aria-pressed={selected}
      onClick={onClick}
      style={{
        ...style,
        appearance: 'none',
        cursor: 'pointer',
        font: 'inherit',
      }}
    >
      {content}
    </button>
  )
}

export function RatioTable({ columns, rows, fit = false }: { columns: string[]; rows: string[][]; fit?: boolean }) {
  return (
    <div className="dtable-wrap" style={{ maxHeight: fit ? undefined : 320, overflowX: fit ? 'hidden' : undefined, overflowY: fit ? 'visible' : undefined }}>
      <table className="dtable" style={fit ? { tableLayout: 'fixed', minWidth: 0 } : undefined}>
        {fit ? (
          <colgroup>
            <col style={{ width: '46%' }} />
            <col style={{ width: '18%' }} />
            <col style={{ width: '18%' }} />
            <col style={{ width: '18%' }} />
          </colgroup>
        ) : null}
        <thead>
          <tr>
            {columns.map((column, idx) => (
              <th key={column} style={idx === 0 ? { textAlign: 'left' } : { textAlign: 'right', whiteSpace: 'nowrap' }}>{column}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.length ? rows.map((row, rowIdx) => (
            <tr key={`${row[0]}-${rowIdx}`}>
              {row.map((cell, idx) => (
                <td key={`${idx}-${cell}`} style={idx === 0 ? { textAlign: 'left', whiteSpace: fit ? 'normal' : undefined } : { textAlign: 'right', fontFamily: 'var(--font-mono)', whiteSpace: 'nowrap' }}>
                  {cell}
                </td>
              ))}
            </tr>
          )) : (
            <tr><td colSpan={columns.length}>No period data available.</td></tr>
          )}
        </tbody>
      </table>
    </div>
  )
}

type RatioChartTextLabel = {
  text: string
  x: number
  y: number
  anchor: 'start' | 'middle' | 'end'
  fill: string
  font: string
}

function RatioChartLabel({ label }: { label: RatioChartTextLabel }) {
  return (
    <text
      x={label.x}
      y={label.y}
      pointerEvents="none"
      textAnchor={label.anchor}
      style={{
        font: label.font,
        fill: label.fill,
        stroke: 'var(--surface)',
        strokeLinecap: 'round',
        strokeLinejoin: 'round',
        strokeWidth: 4,
        paintOrder: 'stroke fill',
      }}
    >
      {label.text}
    </text>
  )
}

export function RatioTrendChart({ ratio, benchmark, benchmarkLabel = 'IRS average' }: { ratio: Pick<RatioCard, 'id' | 'label' | 'unit' | 'trend'>; benchmark: number | null; benchmarkLabel?: string }) {
  const points = ratioTrendTail(ratio, 12)
  const [tooltipIndex, setTooltipIndex] = useState<number | null>(null)
  const finiteValues = points.map((point) => point.value).filter((value) => Number.isFinite(value))
  // Exclude off-the-scale outliers (a near-zero-denominator spike) from the axis DOMAIN so one bad
  // point doesn't crush the scale for the rest; fall back to all finite values if every point is out
  // of range. Out-of-range points are still plotted, clamped to the axis edge by `plotY` below.
  const inRangeValues = finiteValues.filter((value) => !isRatioValueOutOfRange(value, ratio.unit))
  const values = inRangeValues.length ? inRangeValues : finiteValues
  const bench = benchmark != null && Number.isFinite(benchmark) ? benchmark : null
  const domainValues = bench == null ? values : [...values, bench]
  if (points.length < 2 || !domainValues.length) {
    return <div className="cfo-shell-notice">Not enough trend history for this ratio.</div>
  }
  const min = Math.min(...domainValues)
  const max = Math.max(...domainValues)
  const pad = (max - min || Math.abs(max) || 1) * 0.14
  const yMin = min - pad
  const yMax = max + pad
  const width = 560
  const height = 320
  const left = 16
  const right = 16
  const top = 42
  const bottom = 40
  const plotW = width - left - right
  const plotH = height - top - bottom
  const toX = (idx: number) => left + (idx / Math.max(points.length - 1, 1)) * plotW
  const toY = (value: number) => top + ((yMax - value) / (yMax - yMin || 1)) * plotH
  // Clamp plotted position to the plot band so an out-of-range point pins to the edge instead of
  // shooting off-canvas (the domain above is computed from in-range values only).
  const plotY = (value: number) => Math.min(top + plotH, Math.max(top, toY(value)))
  const clampLabelY = (y: number) => Math.min(top + plotH - 8, Math.max(top + 13, y))
  const linePoints = points.map((point, idx) => `${toX(idx).toFixed(1)},${plotY(point.value).toFixed(1)}`).join(' ')
  const areaPath = `M ${toX(0).toFixed(1)},${plotY(points[0].value).toFixed(1)} ` +
    points.slice(1).map((point, idx) => `L ${toX(idx + 1).toFixed(1)},${plotY(point.value).toFixed(1)}`).join(' ') +
    ` L ${toX(points.length - 1).toFixed(1)},${(top + plotH).toFixed(1)} L ${toX(0).toFixed(1)},${(top + plotH).toFixed(1)} Z`
  const benchmarkY = bench == null ? null : toY(bench)
  const gradientId = `fg-trend-grad-${ratio.id}`
  const endpointLabel = (idx: number, anchor: 'start' | 'end'): RatioChartTextLabel => {
    const point = points[idx]
    const neighbor = points[idx === 0 ? 1 : idx - 1] || point
    const y = plotY(point.value)
    const neighborY = plotY(neighbor.value)
    const preferAbove = neighborY > y
    const primaryY = clampLabelY(y + (preferAbove ? -16 : 24))
    const alternateY = clampLabelY(y + (preferAbove ? 24 : -16))
    const labelY = benchmarkY != null && Math.abs(primaryY - benchmarkY) < 18
      ? alternateY
      : primaryY
    return {
      text: `${idx === 0 ? 'Beginning' : 'Ending'} ${formatRatioValue(point.value, ratio.unit)}`,
      x: toX(idx) + (anchor === 'start' ? 8 : -8),
      y: labelY,
      anchor,
      fill: 'var(--brand)',
      font: '850 12.5px var(--font-ui)',
    }
  }
  const firstLabel = endpointLabel(0, 'start')
  const lastLabel = endpointLabel(points.length - 1, 'end')
  const benchmarkChartLabel: RatioChartTextLabel | null = benchmarkY == null ? null : (() => {
    const text = `${benchmarkLabel} ${formatRatioValue(bench, ratio.unit)}`
    const candidates = [
      clampLabelY(benchmarkY - 12),
      clampLabelY(benchmarkY + 22),
      clampLabelY(benchmarkY - 30),
      clampLabelY(benchmarkY + 40),
    ]
    const y = candidates
      .sort((a, b) => Math.abs(b - lastLabel.y) - Math.abs(a - lastLabel.y))[0] || clampLabelY(benchmarkY - 12)
    return {
      text,
      x: left + plotW - 2,
      y,
      anchor: 'end',
      fill: 'var(--ink)',
      font: '850 12px var(--font-ui)',
    }
  })()
  const activeTooltipIndex = tooltipIndex == null ? null : Math.min(Math.max(tooltipIndex, 0), points.length - 1)
  const activePoint = activeTooltipIndex == null ? null : points[activeTooltipIndex]
  const priorPoint = activeTooltipIndex != null && activeTooltipIndex > 0 ? points[activeTooltipIndex - 1] : null
  const tooltipX = activeTooltipIndex == null ? 50 : (toX(activeTooltipIndex) / width) * 100
  const tooltipY = activePoint == null ? 50 : (plotY(activePoint.value) / height) * 100
  const tooltipSide = tooltipX > 72 ? 'left' : tooltipX < 28 ? 'right' : 'center'
  const tooltipChange = activePoint && priorPoint ? activePoint.value - priorPoint.value : null
  const tooltipChangeText = tooltipChange == null
    ? 'First visible point'
    : `${tooltipChange >= 0 ? '+' : ''}${formatRatioValue(tooltipChange, ratio.unit)} vs. prior point`
  const updateTooltipFromPointer = (event: PointerEvent<HTMLDivElement>) => {
    const rect = event.currentTarget.getBoundingClientRect()
    const x = ((event.clientX - rect.left) / Math.max(rect.width, 1)) * width
    const idx = Math.round(((x - left) / Math.max(plotW, 1)) * Math.max(points.length - 1, 1))
    setTooltipIndex(Math.min(Math.max(idx, 0), points.length - 1))
  }

  return (
    <div
      className="fg-ratio-trend"
      tabIndex={0}
      aria-label={`${ratio.label} 12-month trend. Hover or focus for point details.`}
      onPointerEnter={updateTooltipFromPointer}
      onPointerMove={updateTooltipFromPointer}
      onPointerLeave={() => setTooltipIndex(null)}
      onFocus={() => setTooltipIndex(points.length - 1)}
      onBlur={() => setTooltipIndex(null)}
    >
      <svg viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="xMidYMid meet" role="img" aria-label={`${ratio.label} trend versus ${benchmarkLabel}`}>
        <defs>
          <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="var(--brand)" stopOpacity={0.18} />
            <stop offset="100%" stopColor="var(--brand)" stopOpacity={0} />
          </linearGradient>
        </defs>
        <line x1={left} y1={top + plotH} x2={left + plotW} y2={top + plotH} stroke="var(--grid)" strokeWidth={1.5} />
        {benchmarkY != null ? (
          <>
            <line x1={left} y1={benchmarkY} x2={left + plotW} y2={benchmarkY} stroke="var(--muted)" strokeWidth={1.5} strokeDasharray="5 5" />
            {benchmarkChartLabel ? <RatioChartLabel label={benchmarkChartLabel} /> : null}
          </>
        ) : null}
        <path d={areaPath} fill={`url(#${gradientId})`} />
        <polyline points={linePoints} fill="none" stroke="var(--brand)" strokeWidth={3.5} strokeLinecap="round" strokeLinejoin="round" />
        {points.map((point, idx) => {
          const isEnd = idx === 0 || idx === points.length - 1
          return (
            <g key={point.period}>
              {isEnd ? <circle cx={toX(idx)} cy={plotY(point.value)} r={7} fill="var(--surface)" /> : null}
              <circle cx={toX(idx)} cy={plotY(point.value)} r={isEnd ? 5 : 3.6} fill="var(--brand)">
                <title>{`${formatShortMonth(point.period)}: ${formatRatioValue(point.value, ratio.unit)}`}</title>
              </circle>
            </g>
          )
        })}
        <RatioChartLabel label={firstLabel} />
        <RatioChartLabel label={lastLabel} />
        {points.map((point, idx) => (
          idx === 0 || idx === points.length - 1 || idx % 3 === 0 ? (
            <text key={`tick-${point.period}`} x={toX(idx)} y={height - 12} textAnchor={idx === 0 ? 'start' : idx === points.length - 1 ? 'end' : 'middle'} style={{ font: '600 11.5px var(--font-ui)', fill: 'var(--muted)' }}>
              {formatShortMonth(point.period)}
            </text>
          ) : null
        ))}
      </svg>
      {activePoint ? (
        <div
          className={`fg-ratio-trend-tooltip ${tooltipSide}`}
          role="tooltip"
          style={{
            left: `${Math.min(92, Math.max(8, tooltipX))}%`,
            top: `${Math.min(84, Math.max(12, tooltipY))}%`,
          }}
        >
          <div className="fg-ratio-trend-tooltip-period">{formatAsOf(activePoint.period)}</div>
          <div className="fg-ratio-trend-tooltip-value">{formatRatioValue(activePoint.value, ratio.unit)}</div>
          <div className="fg-ratio-trend-tooltip-meta">{tooltipChangeText}</div>
          {bench != null ? (
            <div className="fg-ratio-trend-tooltip-meta">{benchmarkLabel} {formatRatioValue(bench, ratio.unit)}</div>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}

export function RatioDetailCard({
  ratio,
  ratioWindow,
  onRevertAiChange,
  onRedirectAiChange,
  aiChangeBusy,
}: {
  ratio: RatioCard
  ratioWindow: RatioWindow
  onRevertAiChange?: (claimId: string, originId: string) => void
  onRedirectAiChange?: (originId: string, instruction: string) => void
  aiChangeBusy?: boolean
}) {
  const tone = ratioTone(ratio)
  const direction = ratioDirectionGlyph(ratio)
  const companyAverage = ratioCompanyValueForWindow(ratio, ratioWindow)
  const benchmark = ratioBenchmarkAnchor(ratio)
  const benchmarkSource = ratioBenchmarkSource(ratio)
  const benchmarkLabel = ratioBenchmarkSourceLabel(benchmarkSource)
  const explanation = ratio.ai_interpretation || ratio.interpretation
  // The secondary stat: for "Latest month" it becomes a trailing average (so it doesn't duplicate the
  // "Latest" stat); for every other window it's that window's own average.
  const secondaryContext = ratioWindow === 'latest' ? ratioTrailingContext(ratio) : null
  const secondaryWindow: RatioWindow =
    secondaryContext?.window ?? (ratioWindow === 'latest' ? 'trailing_12' : ratioWindow)
  const secondaryValue = ratioWindow === 'latest' ? secondaryContext?.value ?? null : companyAverage

  return (
    <article
      className="card rise fg-ratio-detail"
      style={{ borderLeft: `4px solid ${tone.edge}` }}
    >
      <div className="fg-ratio-detail-head">
        <div className="fg-ratio-detail-intro">
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <h3 style={{ margin: 0, fontSize: 'var(--fs-h2)', letterSpacing: '-0.01em' }}>{ratio.label}</h3>
            <span className="fg-ratio-pill" style={{ color: tone.color, background: tone.bg }}>
              {tone.label}
            </span>
            {direction.glyph ? (
              <span className="fg-ratio-trend-tag" style={{ color: direction.color }}>
                {direction.glyph} {ratioDirectionLabel(ratio)}
              </span>
            ) : null}
            {ratio.ai_change ? (
              <RatioAiChangeBadge
                change={ratio.ai_change}
                onRevert={onRevertAiChange}
                onRedirect={onRedirectAiChange}
                busy={aiChangeBusy}
              />
            ) : null}
            {/* Pin this card into the board pack as a `ratio_card` block. The
                server re-serves the live card on every report render; the
                frozen copy below is the fallback (and captures the values the
                owner saw when pinning). Renders nothing without a provider. */}
            <PinToReportButton
              buildPayload={() => ({
                title: ratio.label,
                kind: 'ratio_card',
                section: 'home',
                source: { ratio_id: ratio.id, window: ratioWindow },
                content: { card: pinnedRatioCardSnapshot(ratio) },
              })}
            />
          </div>
          <p className="fg-ratio-explanation">{normalizeCurrencyTextCompact(explanation)}</p>
          <div className="fg-ratio-formula">{ratio.formula}</div>
        </div>
        <div className="fg-ratio-stats">
          <RatioStat label="Latest" value={formatRatioValue(ratio.latest, ratio.unit)} accent={tone.color} large />
          <RatioStat label={ratioWindowOption(secondaryWindow).label} value={formatRatioValue(secondaryValue, ratio.unit)} large />
          <RatioStat label={benchmarkLabel} value={formatRatioValue(benchmark, ratio.unit)} large />
        </div>
      </div>

      <div className="fg-ratio-grid">
        <div>
          <div className="fg-ratio-section-label">12-month trend</div>
          <RatioTrendChart ratio={ratio} benchmark={benchmark} benchmarkLabel={benchmarkLabel} />
        </div>
        <div>
          <div className="fg-ratio-section-label">Comparison periods</div>
          <div className="fg-ratio-table">
            <RatioTable
              columns={['Period', 'Current', 'Prior', 'Change']}
              rows={buildRatioComparisonRows(ratio)}
              fit
            />
          </div>
        </div>
      </div>

      {ratio.component_trends && ratio.component_trends.length === 2 ? (
        <div className="fg-ratio-components-trend">
          <div className="fg-ratio-section-label">
            What’s moving it — {ratio.component_trends[0].label} vs {ratio.component_trends[1].label}
          </div>
          <DualAxisTrendLine
            series={[ratio.component_trends[0], ratio.component_trends[1]]}
            ariaLabel={`${ratio.label}: ${ratio.component_trends[0].label} versus ${ratio.component_trends[1].label} over the last 12 months`}
          />
        </div>
      ) : null}

      {ratio.components.length ? (
        // Grid, not flex-wrap: RatioStat fills its cell (width/height 100%), so a plain flex row
        // collapses the tiles into one full-width, over-tall stack. Match the overview stat grid.
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12 }}>
          {ratio.components.map((component) => (
            <RatioStat
              key={component.id}
              label={component.label}
              value={formatRatioValue(component.value, component.unit)}
            />
          ))}
        </div>
      ) : null}

      <div className="fg-ratio-detail-foot">
        {ratio.benchmark.note}
      </div>
    </article>
  )
}
